"use client";

// Kessem Loader — Liquid Fill
//
// Scène unique : "KESSEM" en grand, lettres "vides" (gris très sombre) que
// remplit un liquide gris clair qui monte du bas jusqu'à 100 %. Une vague
// SVG défile en boucle pour donner la sensation de fluide en mouvement.
// En bas à droite : "loading...  XX %" en mono.
//
// Une fois plein, un éclair blanc scelle l'arrivée d'une étincelle cyan
// qui glisse vers la position exacte du sparkle du logo dans le header
// (handoff mesuré sur #kessem-header-logo). Le backdrop fade out, le site
// se révèle.
//
// Mécaniques transverses : cookie 24h, ESC / bouton Skip, scroll lock,
// reduced-motion bypass, force-loader=1, focus restauré sur le logo.

import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  MotionConfig,
} from "motion/react";
import { Bebas_Neue } from "next/font/google";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const bebasNeue = Bebas_Neue({
  subsets: ["latin"],
  weight: ["400"],
  display: "swap",
});

// Pas de gating : le loader s'affiche systématiquement à chaque chargement.

// ─── Timeline ──────────────────────────────────────────────────────────
// Valeurs raccourcies (~30 %) par rapport à la version 4.65 s pour une
// meilleure perception mobile sans perdre le drama du remplissage liquide.
const LIQUID_DURATION = 2.2;       // remplissage 0 → 100 %
const HOLD_DURATION = 0.25;        // pause une fois plein
const ZOOM_START = LIQUID_DURATION + HOLD_DURATION;
const ZOOM_DURATION = 0.85;        // zoom-forward dans les lettres
const BG_FADE_DELAY = 0.35;        // backdrop part dans la 2e moitié du zoom
const BG_FADE_DURATION = 0.5;
const TOTAL_DURATION = ZOOM_START + ZOOM_DURATION + 0.05;

const WORD = "KESSEM";

// Module-level guard: once the loader has played to completion in this
// page-load session, any subsequent re-mount (StrictMode resilience test,
// Suspense boundary thrash, i18n re-hydration, Lenis tree changes...) skips
// the animation entirely. Survives React re-mounts but is reset on a true
// page reload, which is the correct UX: each visitor sees the loader once.
let HAS_PLAYED = false;

function rand(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Wave path ──────────────────────────────────────────────────────────
// Génère un chemin sinusoïdal qui se boucle parfaitement quand on translate
// X de -PERIOD : on dessine 2× la période, on scrolle d'1× → tile parfait.
function buildWavePath(width: number, period: number, amplitude: number, depth: number) {
  const total = width + period * 2;
  const segments = Math.ceil(total / (period / 4));
  let d = `M ${-period} ${depth + amplitude}`;
  for (let i = 0; i <= segments; i++) {
    const x = -period + i * (period / 4);
    const y = amplitude + amplitude * Math.sin((x / period) * Math.PI * 2);
    d += ` L ${x} ${y}`;
  }
  d += ` L ${total} ${depth} L ${-period} ${depth} Z`;
  return d;
}

// ─── Liquid word ────────────────────────────────────────────────────────

function LiquidWord({ progress }: { progress: ReturnType<typeof useMotionValue<number>> }) {
  // Géométrie SVG. viewBox large pour laisser respirer.
  const VB_W = 1200;
  const VB_H = 280;
  const FONT_SIZE = 240;
  const TEXT_Y = 210; // baseline

  // Hauteur de remplissage : 0 → VB_H selon progress (0..1).
  const fillY = useTransform(progress, (p) => VB_H * (1 - p));

  // Vague — translate X en boucle infinie côté JS via useMotionValue.
  const waveX = useMotionValue(0);
  useEffect(() => {
    const controls = animate(waveX, [-0, -260], {
      duration: 2.4,
      ease: "linear",
      repeat: Infinity,
    });
    return () => controls.stop();
  }, [waveX]);

  const wavePath = useMemo(
    () => buildWavePath(VB_W, 260, 9, VB_H + 40),
    [],
  );

  const textProps = {
    x: VB_W / 2,
    y: TEXT_Y,
    textAnchor: "middle" as const,
    fontSize: FONT_SIZE,
    fontFamily: "var(--font-loader)",
    letterSpacing: "0.02em",
    fontWeight: 400,
  };

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className={`${bebasNeue.className} w-[min(92vw,1100px)]`}
      style={{ ["--font-loader" as string]: bebasNeue.style.fontFamily }}
      aria-hidden
    >
      <defs>
        <clipPath id="kessem-text-clip">
          <text {...textProps}>{WORD}</text>
        </clipPath>
      </defs>

      {/* Empty state — lettres "creuses" gris très sombre. */}
      <text {...textProps} fill="#1c1c1c">
        {WORD}
      </text>

      {/* Liquide — clipé par le texte. Une nappe rectangle dont le sommet
          est masqué par une vague SVG qui défile horizontalement. */}
      <g clipPath="url(#kessem-text-clip)">
        <motion.g style={{ y: fillY }}>
          {/* Bloc plein (sous la vague). */}
          <rect x={0} y={40} width={VB_W} height={VB_H + 80} fill="#d6d6d6" />
          {/* Vague — duplique le path puis translate X pour boucle parfaite. */}
          <motion.path d={wavePath} fill="#d6d6d6" style={{ x: waveX }} />
          {/* 2e vague décalée, plus claire, pour donner du volume. */}
          <motion.path
            d={wavePath}
            fill="#ededed"
            style={{ x: waveX, y: -2 }}
            opacity={0.55}
          />
        </motion.g>
      </g>
    </svg>
  );
}

// ─── Root ───────────────────────────────────────────────────────────────

export function KessemLoader({ onDone }: { onDone?: () => void }) {
  // Skip the loader entirely on subsequent re-mounts within the same page
  // load (see HAS_PLAYED above). Also skip when the OS has reduced-motion
  // enabled — same UX: instant site reveal + immediately fire the handoff
  // event so every gated subscriber wakes up.
  const [show, setShow] = useState(() => !HAS_PLAYED);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!HAS_PLAYED && !reduced) return;
    HAS_PLAYED = true;
    (window as unknown as { __kessemLoaderDone?: boolean }).__kessemLoaderDone = true;
    window.dispatchEvent(new Event("kessem:loader-done"));
    setShow(false);
    onDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showSkip, setShowSkip] = useState(false);
  const [phaseMessage, setPhaseMessage] = useState("Chargement en cours");

  // Progress 0→1 piloté par animate() — partagé entre la liquid mask et le
  // compteur "%" pour qu'ils restent rigoureusement synchronisés.
  const progress = useMotionValue(0);
  const percent = useTransform(progress, (p) => Math.round(p * 100));
  // Write the percent text directly to the DOM via a ref instead of
  // triggering a React setState on every change. The motion value changes
  // ~100 times during the 2.2 s fill; routing each through React causes a
  // full re-render of the loader component (with all its motion children)
  // every time, which alone was enough to cost a few ms per frame on
  // a mid-range CPU.
  const percentTextRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const unsub = percent.on("change", (v) => {
      const el = percentTextRef.current;
      if (el) el.textContent = String(v);
    });
    return unsub;
  }, [percent]);

  // Lance le remplissage dès que le loader est monté.
  useEffect(() => {
    if (!show) return;
    const controls = animate(progress, 1, {
      duration: LIQUID_DURATION,
      ease: [0.65, 0, 0.35, 1],
    });
    return () => controls.stop();
  }, [show, progress]);

useEffect(() => {
    if (!show) return;
    // Désactive la restauration de scroll du navigateur et force le top à
    // chaque chargement — l'utilisateur arrive toujours en haut du site.
    const prevScrollRestoration = window.history.scrollRestoration;
    try {
      window.history.scrollRestoration = "manual";
    } catch {
      /* ignore */
    }
    window.scrollTo(0, 0);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      try {
        window.history.scrollRestoration = prevScrollRestoration;
      } catch {
        /* ignore */
      }
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const tShow = setTimeout(() => setShowSkip(true), 800);
    const tHide = setTimeout(() => setShowSkip(false), ZOOM_START * 1000);
    const tMsg = setTimeout(
      () => setPhaseMessage("Site chargé"),
      ZOOM_START * 1000,
    );
    return () => {
      clearTimeout(tShow);
      clearTimeout(tHide);
      clearTimeout(tMsg);
    };
  }, [show]);

  const restoreFocus = useCallback(() => {
    const target = document.getElementById("kessem-header-logo");
    if (target instanceof HTMLElement) {
      queueMicrotask(() => target.focus({ preventScroll: true }));
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    // Hand off to the rest of the app at the start of the backdrop fade-out
    // so the SceneBackground can mount and finish its shader compile during
    // the 0.5 s fade — by the time the loader is fully transparent the scene
    // is already painted. Without this, the scene only mounts after the
    // loader unmounts and the user sees a brief white flash.
    const tHandoff = setTimeout(() => {
      // Window flag lets subscribers that mount AFTER the event was
      // already dispatched (HMR, late hydration) still detect handoff.
      // HAS_PLAYED is flipped here too (not only at TOTAL_DURATION) so
      // that any remount during the fade-out window doesn't restart the
      // animation from 0.
      HAS_PLAYED = true;
      (window as unknown as { __kessemLoaderDone?: boolean }).__kessemLoaderDone = true;
      window.dispatchEvent(new Event("kessem:loader-done"));
    }, (ZOOM_START + BG_FADE_DELAY) * 1000);
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
      HAS_PLAYED = true;
      setShow(false);
      onDone?.();
      restoreFocus();
    }, TOTAL_DURATION * 1000);
    return () => {
      clearTimeout(tHandoff);
      clearTimeout(t);
    };
  }, [show, onDone, restoreFocus]);

  const skip = useCallback(() => {
    window.scrollTo(0, 0);
    HAS_PLAYED = true;
    (window as unknown as { __kessemLoaderDone?: boolean }).__kessemLoaderDone = true;
    window.dispatchEvent(new Event("kessem:loader-done"));
    setShow(false);
    onDone?.();
    restoreFocus();
  }, [onDone, restoreFocus]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, skip]);

  // Ambient particles removed — each was a separate motion.span with its
  // own JS animation driver. 12 drivers running in parallel during a phase
  // where the main thread is already saturated by hydration cost too much
  // for too little visual payoff. The liquid fill carries the moment alone.
  // Keep the rand() seed import referenced so the helper isn't tree-shaken
  // away if a future redesign needs the field of points back.
  void rand;

  if (!show) return null;

  // Force the loader to animate even if the user has OS-level reduced
  // motion enabled. The page itself still respects that preference (via
  // MotionGate's "user" setting post-handoff); only the loader overrides
  // it so the liquid fill, wave, and zoom always play.
  return (
    <MotionConfig reducedMotion="never">
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Chargement du site Kessem"
      dir="ltr"
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
    >
      {/* Backdrop sombre — fade out sur le handoff. */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{
          duration: BG_FADE_DURATION,
          delay: ZOOM_START + BG_FADE_DELAY,
          ease: [0.65, 0, 0.35, 1],
        }}
        className="absolute inset-0 bg-[#0d0d0f]"
        aria-hidden
      />

      {/* Bloc central : KESSEM liquide + ligne loading. Une fois plein, le
          bloc plonge en avant (scale up) et fade en sortie pour donner un
          effet "on traverse les lettres" et révéler le site. */}
      <motion.div
        initial={{ scale: 1, opacity: 1 }}
        animate={{
          scale: [1, 1, 14],
          opacity: [1, 1, 0],
        }}
        transition={{
          duration: ZOOM_START + ZOOM_DURATION,
          times: [0, ZOOM_START / (ZOOM_START + ZOOM_DURATION), 1],
          ease: [0.55, 0, 0.2, 1],
          opacity: {
            duration: ZOOM_START + ZOOM_DURATION,
            times: [
              0,
              (ZOOM_START + ZOOM_DURATION * 0.45) / (ZOOM_START + ZOOM_DURATION),
              1,
            ],
            ease: [0.4, 0, 0.6, 1],
          },
        }}
        className="relative flex flex-col items-center"
        style={{ willChange: "transform, opacity", transformOrigin: "50% 50%" }}
      >
        <LiquidWord progress={progress} />

        {/* Ligne "loading... XX %" — alignée à droite, sous la lettre M. */}
        <div className="mt-3 flex w-[min(92vw,1100px)] justify-end pe-[6%]">
          <div className="flex items-baseline gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55">
            <span>loading…</span>
            <span className="tabular-nums text-white/80">
              <span
                ref={percentTextRef}
                className="inline-block min-w-[2ch] text-end"
              >
                0
              </span>
              <span className="ms-1">%</span>
            </span>
          </div>
        </div>
      </motion.div>

      {/* Skip */}
      <motion.button
        type="button"
        onClick={skip}
        initial={{ opacity: 0 }}
        animate={{ opacity: showSkip ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-6 right-6 rounded-full border border-white/15 bg-white/[0.03] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        aria-label="Passer l'animation de chargement (ESC)"
      >
        Skip <span aria-hidden className="ms-1 opacity-60">esc</span>
      </motion.button>

      <span className="sr-only" aria-live="polite">
        {phaseMessage}
      </span>
    </div>
    </MotionConfig>
  );
}

export default KessemLoader;
